/**
 * LastNightCard — the "Latest nightly run" box under the group chart
 * (TRA-1402).
 *
 * The api reads the night with the rule the nightly job's #platform line
 * is built on (`latest_night`): the night's own score on the climb over
 * nightly runs, the change against the night before it, how many tasks
 * ran, and which tasks got worse or are new. This card only says it. It
 * shows the newest night however old it is (nights run every other day
 * and only when staff start one, so the newest can be weeks old) and
 * renders nothing only when no night has scored the group. Dates print
 * the way the chart's axis prints them, so the card and the chart name
 * the same day.
 */

import { Link } from 'react-router-dom'

import { ShowMore } from '@/components/billing/ShowMore'
import { formatAxisDay } from '@/components/evals/climb'
import { formatScore, type ScoreFormat } from '@/components/evals/format'
import { formatDelta } from '@/components/evals/lastNight'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { GroupNight, NightTask } from '@/api/generated/model'

/** Tasks a list names before the reader asks for the rest. */
const LISTED_TASKS = 10

interface LastNightCardProps {
  night: GroupNight | null | undefined
  groupSlug: string
  format: ScoreFormat
}

/** The dates under the title: the night, and the night it is compared with. */
function nightCaption(night: GroupNight): string {
  const day = formatAxisDay(new Date(night.date).getTime())
  if (night.previous_date === null) {
    return `${day}. This is the first night, so there is no earlier night to compare it with.`
  }
  const previous = formatAxisDay(new Date(night.previous_date).getTime())
  if (night.score === null) {
    return `${day} compared with ${previous}. It scored none of the tests the group is scored on, so it has no score or change against that night.`
  }
  return `${day} compared with ${previous}`
}

/** The change stat: the signed change, "First night" when there is no earlier night, or the null placeholder when the night has no score to compare. */
function changeValue(night: GroupNight, format: ScoreFormat): string {
  if (night.delta !== null) return formatDelta(night.delta, format)
  return night.previous_date === null ? 'First night' : formatScore(null, format)
}

function NightStat({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <dt className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1 whitespace-nowrap font-mono text-lg tabular-nums text-foreground">{value}</dd>
    </div>
  )
}

function TaskList({
  heading,
  tasks,
  groupSlug,
  noun,
  empty,
}: {
  heading: string
  tasks: NightTask[]
  groupSlug: string
  /** What the rows are, for the disclosure's accessible name. */
  noun: string
  /** The sentence shown when the list is empty. */
  empty: string
}): React.JSX.Element {
  return (
    <div>
      <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
        {heading} ({tasks.length.toString()})
      </h3>
      {tasks.length === 0 ? (
        <p className="mt-1 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <div className="mt-1">
          <ShowMore rows={tasks} initial={LISTED_TASKS} noun={noun}>
            {(visible) => (
              <ul className="space-y-1">
                {visible.map((task) => (
                  <li key={task.slug}>
                    <Link
                      to={`/evals/groups/${groupSlug}/tasks/${task.slug}`}
                      className="text-sm text-foreground hover:text-primary hover:underline"
                    >
                      {task.display_name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </ShowMore>
        </div>
      )}
    </div>
  )
}

export function LastNightCard({
  night,
  groupSlug,
  format,
}: LastNightCardProps): React.JSX.Element | null {
  if (night === null || night === undefined) return null
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">Latest nightly run</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">{nightCaption(night)}</p>
        <dl className="grid gap-3 sm:grid-cols-3">
          <NightStat label="Score" value={formatScore(night.score, format)} />
          <NightStat label="Change" value={changeValue(night, format)} />
          <NightStat label="Tasks run" value={night.tasks_run.toString()} />
        </dl>
        <div className="grid gap-4 sm:grid-cols-2">
          <TaskList
            heading="Got worse"
            tasks={night.regressions}
            groupSlug={groupSlug}
            noun="tasks that got worse"
            empty="No task scored lower than on the night before."
          />
          <TaskList
            heading="New"
            tasks={night.new_tasks}
            groupSlug={groupSlug}
            noun="new tasks"
            empty="No task ran for the first time."
          />
        </div>
      </CardContent>
    </Card>
  )
}
